import { DateTime, Duration } from "luxon";

import type { WorkspaceIntegrationType } from "@storyboard/web-client";
import { vendorLabel } from "@/utils/integrationLabels";

/** How old a location may be before the roster and Dispatch grey it; the sibling of the ten-minute offline threshold. */
export const LOCATION_STALE_AFTER = Duration.fromObject({ minutes: 15 });

/** How long a pointer rests on a location before its tooltip opens; the one knob for all three surfaces. */
export const LOCATION_TOOLTIP_ENTER_DELAY: Duration = Duration.fromObject({ milliseconds: 500 });

/** The source named for a location no ELD gave: the phone, reporting through Storyboard. */
const PHONE_SOURCE_LABEL = "Storyboard";

/** The units an age is shown in, largest first; the label shows the first one with a non-zero value, alone. */
const AGE_UNITS = ["days", "hours", "minutes"] as const;

/**
 * How stale a driver's location is at one moment.
 */
export type LocationStaleness = {
  /** True past the threshold, and whenever the age is unknown. */
  stale: boolean;
  /** Time from the fix to `now`, floored at zero; null when the timestamp is missing or unparseable. */
  age: Duration | null;
};

/**
 * Judges a location fixed at `locationUpdatedAt` against `now`, the surface's minute clock; never reads the clock itself.
 */
export function locationStaleness(locationUpdatedAt: string | null | undefined, now: number): LocationStaleness {
  const fixedAt = DateTime.fromISO(locationUpdatedAt ?? "");
  if (!fixedAt.isValid) return { stale: true, age: null };
  const age = Duration.fromMillis(Math.max(0, DateTime.fromMillis(now).diff(fixedAt).toMillis()));
  return { stale: age.toMillis() > LOCATION_STALE_AFTER.toMillis(), age };
}

/**
 * The hover text for a location, fresh or stale: its source, then its age, then "(stale)" past the threshold.
 * "Platform Science · 2 min ago", "Storyboard · 3 days ago (stale)", "Storyboard · age unknown (stale)".
 */
export function locationProvenanceLabel(staleness: LocationStaleness, source: WorkspaceIntegrationType | null): string {
  const sourceLabel = source === null ? PHONE_SOURCE_LABEL : vendorLabel(source);
  const suffix = staleness.stale ? " (stale)" : "";
  return `${sourceLabel} · ${ageLabel(staleness.age)}${suffix}`;
}

/**
 * An age in its largest non-zero unit alone, "2 min ago", "1 hr ago", "3 days ago"; "just now" when it rounds to no
 * minutes, "age unknown" when it is null.
 */
function ageLabel(age: Duration | null): string {
  if (age === null) return "age unknown";
  // Round to the nearest minute before splitting, so fifty-nine and a half minutes reads as an hour.
  const rounded = Duration.fromObject({ minutes: Math.round(age.as("minutes")) }).shiftTo(...AGE_UNITS);
  // Show the largest unit with a value and drop the smaller ones rather than folding them in.
  for (const unit of AGE_UNITS) {
    const value = rounded.get(unit);
    if (value > 0) return `${Duration.fromObject({ [unit]: value }).toHuman({ unitDisplay: "short" })} ago`;
  }
  // Every unit is zero: Luxon renders a zero duration as an empty string, so name the case instead.
  return "just now";
}
